import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty, CommandGroup } from "@/components/ui/command";
import { Phone, Check, ChevronsUpDown, Search as SearchIcon, UserCheck, AlertTriangle } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Req, SectionHeader, FieldError, invalidCls } from "@/components/newTicket/parts";
import ChannelPill from "@/components/settings/ChannelPill";
import CustomerHistoryIndicator from "@/components/CustomerHistoryIndicator";
import { expectedDigits, requiredDigits, phoneLengthError, phoneHintLabel } from "@/lib/phoneFormat";

export default function ContactLookupSection({
  form, setForm,
  countryCodes, contactModes, channelsList, languages,
  customerMatches, matchedCustomer, onPickCustomer, onUnlinkCustomer,
  onViewHistory, historyOpen, validation = {},
}) {
  const [ccOpen, setCcOpen] = useState(false);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const err = (k) => validation.err?.(k);
  const touch = (k) => validation.touch?.(k);
  const cnErr = err("contact_number");

  return (
    <section data-testid="section-contact-lookup">
      <SectionHeader letter="A" title="Contact Lookup" icon={Phone} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div>
          <Label className="text-xs font-semibold text-slate-700">
            Contact Number<Req />
          </Label>
          <div className="flex gap-2 mt-1.5">
            <Popover open={ccOpen} onOpenChange={setCcOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="country-code-trigger"
                  className="w-[140px] h-10 px-3 inline-flex items-center justify-between border border-slate-300 rounded-md bg-white text-sm hover:border-slate-400"
                >
                  <span className="font-mono text-slate-800 truncate">
                    {form.country_code} {form.country_label}
                  </span>
                  <ChevronsUpDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search country..." data-testid="cc-search" />
                  <CommandList className="max-h-72">
                    <CommandEmpty>No country found.</CommandEmpty>
                    <CommandGroup>
                      {countryCodes.map((c) => (
                        <CommandItem
                          key={c.id}
                          value={`${c.code} ${c.country}`}
                          onSelect={() => {
                            setForm({ ...form, country_code: c.code, country_label: c.country });
                            setCcOpen(false);
                          }}
                          data-testid={`cc-option-${c.country}`}
                        >
                          <Check
                            className={`w-3.5 h-3.5 mr-2 ${form.country_code === c.code ? "opacity-100" : "opacity-0"}`}
                          />
                          <span className="font-mono text-xs text-slate-500 mr-2 w-16">{c.code}</span>
                          <span className="text-sm">{c.country}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <Input
              data-testid="contact-number-input"
              value={form.contact_number}
              onBlur={() => touch("contact_number")}
              onChange={(e) => {
                const cap = requiredDigits(form.country_code, countryCodes) || 15;
                setForm({ ...form, contact_number: e.target.value.replace(/\D/g, "").slice(0, cap) });
              }}
              placeholder={
                requiredDigits(form.country_code, countryCodes)
                  ? `${requiredDigits(form.country_code, countryCodes)} digits`
                  : `${expectedDigits(form.country_code, countryCodes)} digits`
              }
              maxLength={requiredDigits(form.country_code, countryCodes) || 15}
              className={`flex-1 min-w-[120px] h-10 font-mono ${invalidCls(cnErr)}`}
            />
          </div>

          {form.contact_number && phoneLengthError(form.country_code, form.contact_number, countryCodes) ? (
            <p className="text-[11px] text-rose-600 mt-1" data-testid="contact-number-error">
              {phoneLengthError(form.country_code, form.contact_number, countryCodes)}
            </p>
          ) : (
            <FieldError name="contact_number" msg={!form.contact_number ? cnErr : undefined} />
          )}

          {customerMatches.length > 0 && !matchedCustomer && (
            <div className="mt-2 border border-slate-200 rounded-lg shadow-sm bg-white overflow-hidden"
                 data-testid="customer-autocomplete">
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-100 flex items-center gap-1.5">
                <SearchIcon className="w-3 h-3" /> Existing customers — click to pull details
              </div>
              <ul className="max-h-44 overflow-y-auto divide-y divide-slate-100">
                {customerMatches.map((c) => (
                  <li key={c.id}>
                    <button type="button" onClick={() => onPickCustomer(c)}
                            data-testid={`customer-match-${c.id}`}
                            className="w-full text-left px-3 py-2 hover:bg-emerald-50/40 group">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium text-slate-800 truncate">{c.name || "(no name)"}</div>
                        <span className="text-[10px] font-mono text-slate-500 shrink-0">{c.phone_e164 || c.phone}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-slate-500 mt-0.5">
                        <span>{c.email || "—"}</span>
                        <span>·</span>
                        <span>{c.ticket_count || 0} ticket{c.ticket_count === 1 ? "" : "s"}</span>
                        {c.last_ticket_at && <><span>·</span><span>last {new Date(c.last_ticket_at).toLocaleDateString()}</span></>}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {matchedCustomer && (
            <div className="mt-2 inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md px-2.5 py-1.5 text-[11px]"
                 data-testid="customer-matched-pill">
              <UserCheck className="w-3.5 h-3.5" />
              Linked to existing customer <span className="font-semibold">{matchedCustomer.name}</span>
              <button type="button"
                      onClick={() => setUnlinkOpen(true)}
                      data-testid="unlink-customer-btn"
                      className="ml-1 text-emerald-700 hover:text-emerald-900 underline text-[10px]">
                Unlink
              </button>
            </div>
          )}

          <AlertDialog open={unlinkOpen} onOpenChange={setUnlinkOpen}>
            <AlertDialogContent data-testid="unlink-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Unlink this customer?</AlertDialogTitle>
                <AlertDialogDescription>
                  This detaches <span className="font-semibold">{matchedCustomer?.name || "the customer"}</span> from
                  this ticket draft — their saved details won&apos;t auto-fill. You can re-link by re-entering the number.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="unlink-cancel-btn">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="unlink-confirm-btn"
                  className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
                  onClick={() => { onUnlinkCustomer?.(); setUnlinkOpen(false); }}
                >
                  Unlink
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          {matchedCustomer?.notes && (
            <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 flex items-start gap-2"
                 data-testid="new-ticket-sticky-notes">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Sticky notes</div>
                <div className="text-xs text-amber-900 mt-0.5 whitespace-pre-wrap">{matchedCustomer.notes}</div>
              </div>
            </div>
          )}

          <p className="text-[11px] text-slate-500 mt-1.5">
            {phoneHintLabel(form.country_code, countryCodes)} · Tab to search
          </p>
          <CustomerHistoryIndicator
            countryCode={form.country_code}
            contactNumber={form.contact_number}
            onViewHistory={onViewHistory}
          />
        </div>

        <div>
          <Label className="text-xs font-semibold text-slate-700">Contact Mode<Req /></Label>
          <Select
            value={form.contact_mode}
            onValueChange={(v) => { setForm({ ...form, contact_mode: v }); touch("contact_mode"); }}
          >
            <SelectTrigger
              className={`mt-1.5 h-10 ${invalidCls(err("contact_mode"))}`}
              data-testid="contact-mode-select"
              onBlur={() => touch("contact_mode")}
            >
              <SelectValue placeholder="Select contact mode" />
            </SelectTrigger>
            <SelectContent>
              {contactModes.map((m) => {
                const ch = channelsList.find((c) => c.name.toLowerCase() === m.name.toLowerCase());
                return (
                  <SelectItem key={m.id} value={m.name}>
                    <ChannelPill channel={ch} name={m.name} />
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <FieldError name="contact_mode" msg={err("contact_mode")} />
        </div>

        <div>
          <Label className="text-xs font-semibold text-slate-700">Language<Req /></Label>
          <Select
            value={form.language}
            onValueChange={(v) => { setForm({ ...form, language: v }); touch("language"); }}
          >
            <SelectTrigger
              className={`mt-1.5 h-10 ${invalidCls(err("language"))}`}
              data-testid="language-select"
              onBlur={() => touch("language")}
            >
              <SelectValue placeholder="Select language" />
            </SelectTrigger>
            <SelectContent>
              {languages.map((l) => (
                <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError name="language" msg={err("language")} />
        </div>
      </div>
    </section>
  );
}
